import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AmountInput from "@/components/AmountInput";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Download, Send, Lock, Unlock, AlertTriangle, Plus, Trash2, CheckCircle2, RotateCcw, Layers } from "lucide-react";
import { toast } from "sonner";
import { CompanyTotalsCard } from "@/components/CompanyTotalsCard";
import MultiPolizzaDialog from "@/components/MultiPolizzaDialog";

const METHODS_BASIC = ["Contante", "Assegno", "Bancomat", "Bonifico", "Bollettino postale"];
const METHODS_AGENCY = ["Recupero contante", "Recupero assegno", "Sospeso"];
// Metodi di sistema: valorizzabili solo dal backend (es. "Vers. collab." creato
// automaticamente in fase di convalida versamento). NON selezionabili nel dropdown
// ma da mostrare come label neutra (non "legacy") sulla riga.
const SYSTEM_METHODS = ["Vers. collab."];

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function formatDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" });
  } catch { return iso; }
}
function emptyDraft() {
  return { urgente: false, contraente: "", numero_polizza: "", importo: "", metodo_pagamento: "Contante", note: "" };
}

function StatusBadge({ stato }) {
  const map = {
    aperto: { label: "Aperto", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    chiuso_auto: { label: "Chiuso automaticamente", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    chiuso_manuale: { label: "Chiuso manualmente", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    riaperto: { label: "Riaperto", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const it = map[stato] || { label: stato, cls: "bg-slate-100" };
  return <Badge variant="outline" className={`${it.cls} rounded-full px-3 py-0.5 text-xs font-semibold`} data-testid="detail-status">{it.label}</Badge>;
}

export default function SheetDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [multiOpen, setMultiOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cash-sheets/${id}`);
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Ricarica il foglio quando l'agenzia viene aggiornata (es. toggle Plurimandatari
  // in /admin/abbonamento). Senza questo listener, la colonna Compagnia e la
  // validazione "compagnia obbligatoria" restano appese ai valori vecchi.
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener("agency:updated", handler);
    return () => window.removeEventListener("agency:updated", handler);
  }, [load]);

  if (loading) return <div className="text-slate-500 text-sm">Caricamento…</div>;
  if (!data) return null;
  const { sheet, entries, user, totals } = data;
  const agency = data.agency || {};
  const companies = data.companies || [];
  const multiEnabled = !!agency.multi_polizza_enabled;
  const plurimandatari = !!agency.plurimandatari_abilitato;
  const isClosed = sheet.stato === "chiuso_auto" || sheet.stato === "chiuso_manuale";
  const canEdit = !isClosed; // agente can edit open/riaperto

  const close = async () => {
    if (!window.confirm("Chiudere manualmente il foglio cassa? Verrà inviato il riepilogo via email.")) return;
    try { await api.post(`/cash-sheets/${id}/close`); toast.success("Foglio chiuso"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const reopen = async () => {
    if (!window.confirm("Riaprire il foglio cassa per consentire modifiche?")) return;
    try { await api.post(`/cash-sheets/${id}/reopen`); toast.success("Foglio riaperto"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const resend = async () => {
    try { await api.post(`/cash-sheets/${id}/resend`); toast.success("Email reinviata"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const dl = async (kind) => {
    try {
      const res = await api.get(`/cash-sheets/${id}/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foglio_cassa_${user?.cognome || ""}_${sheet?.data_foglio_cassa}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addEntry = async () => {
    if (!draft.contraente.trim() || !draft.numero_polizza.trim()) {
      toast.error("Contraente e numero polizza sono obbligatori");
      return;
    }
    const importo = parseFloat(String(draft.importo).replace(",", "."));
    if (!isFinite(importo)) { toast.error("Importo non valido"); return; }
    setSaving(true);
    try {
      await api.post(`/cash-sheets/${id}/entries`, {
        ...draft,
        importo,
        abbuono: allowAbbuoni ? (parseFloat(String(draft.abbuono || 0).replace(",", ".")) || 0) : 0,
      });
      setDraft(emptyDraft());
      toast.success("Riga aggiunta");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const updateEntry = async (entry, patch) => {
    try { await api.put(`/cash-sheets/${id}/entries/${entry.id}`, patch); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteEntry = async (entry) => {
    if (!window.confirm("Eliminare questa riga?")) return;
    try { await api.delete(`/cash-sheets/${id}/entries/${entry.id}`); toast.success("Riga eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const markVersato = async (entry) => {
    try { await api.post(`/cash-entries/${entry.id}/mark-versato`); toast.success("Riga segnata come versata"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const unmarkVersato = async (entry) => {
    try { await api.post(`/cash-entries/${entry.id}/unmark-versato`); toast.success("Versamento annullato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const isAgentSheet = user?.role === "agente";
  const allowAbbuoni = isAgentSheet || !!user?.abbuoni_abilitati;
  const METHODS = isAgentSheet ? [...METHODS_BASIC, ...METHODS_AGENCY] : METHODS_BASIC;
  const totalsToShow = isAgentSheet
    ? [...METHODS_BASIC, ...METHODS_AGENCY.filter((m) => Number(totals[m] || 0) > 0)]
    : METHODS_BASIC;
  const titleLabel = isAgentSheet ? "FOGLIO CASSA DI AGENZIA" : `FOGLIO CASSA DI ${(user?.nome + " " + user?.cognome).toUpperCase()}`;

  return (
    <div className="space-y-6">
      <Link to="/admin/storico" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-storico">
        <ArrowLeft className="w-4 h-4 mr-1" /> Torna allo storico
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Dettaglio</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
            {titleLabel} DEL {formatDateIT(sheet.data_foglio_cassa)}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <StatusBadge stato={sheet.stato} />
            {sheet.closed_at && <span className="text-xs text-slate-500">Chiuso il {formatDT(sheet.closed_at)} da {sheet.closed_by}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => dl("pdf")} data-testid="dl-pdf"><Download className="w-4 h-4 mr-2" /> PDF</Button>
          <Button variant="outline" onClick={() => dl("excel")} data-testid="dl-xls"><Download className="w-4 h-4 mr-2" /> Excel</Button>
          <Button variant="outline" onClick={resend} data-testid="resend-btn"><Send className="w-4 h-4 mr-2" /> Reinvia email</Button>
          {isClosed ? (
            <Button onClick={reopen} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="reopen-btn"><Unlock className="w-4 h-4 mr-2" /> Riapri</Button>
          ) : (
            <Button onClick={close} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="close-btn"><Lock className="w-4 h-4 mr-2" /> Chiudi ora</Button>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {totalsToShow.map((m) => (
          <Card key={m} className="p-4 border-slate-200">
            <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{m}</div>
            <div className="text-lg font-heading font-bold text-slate-900 mt-1 font-mono-num">{formatEUR(totals[m])}</div>
          </Card>
        ))}
        <Card className="p-4 bg-slate-900 text-white col-span-2 md:col-span-4 lg:col-span-1">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-300">Totale</div>
          <div className="text-xl font-heading font-bold mt-1 font-mono-num">{formatEUR(totals.totale_generale)}</div>
        </Card>
      </div>

      <CompanyTotalsCard sheet={sheet} groups={data.per_compagnia} cognome={user?.cognome} />

      {/* Entries — inline editable when sheet is open */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-10">#</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-16 text-center">Urg.</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Contraente</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">N. Polizza</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-44">Metodo</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right w-32">Importo</th>
                {allowAbbuoni && <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right w-24">Abbuono</th>}
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Note</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-32">Versamento</th>
                <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.length === 0 && <tr><td colSpan={allowAbbuoni ? 10 : 9} className="px-3 py-8 text-center text-slate-500">Nessun incasso registrato.</td></tr>}
              {entries.map((e, i) => {
                const manualVersato = (e.versamento_id || "").startsWith("manual-");
                return (
                  <tr key={e.id} className={e.urgente ? "urgent-row" : "hover:bg-slate-50"} data-testid={`detail-entry-${i}`}>
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2 text-center">
                      <Checkbox
                        checked={!!e.urgente}
                        disabled={!canEdit || e.versato}
                        onCheckedChange={(v) => updateEntry(e, { urgente: !!v })}
                      />
                      {e.urgente && <AlertTriangle className="w-3.5 h-3.5 text-red-600 inline-block ml-1" />}
                    </td>
                    <td className="px-3 py-2">
                      <Input disabled={!canEdit || e.versato} defaultValue={e.contraente} className="h-8 text-sm"
                        onBlur={(ev) => ev.target.value !== e.contraente && updateEntry(e, { contraente: ev.target.value })} />
                    </td>
                    <td className="px-3 py-2">
                      <Input disabled={!canEdit || e.versato} defaultValue={e.numero_polizza} className="h-8 text-sm font-mono text-xs"
                        onBlur={(ev) => ev.target.value !== e.numero_polizza && updateEntry(e, { numero_polizza: ev.target.value })} />
                    </td>
                    <td className="px-3 py-2">
                      <Select disabled={!canEdit || e.versato} value={e.metodo_pagamento} onValueChange={(v) => updateEntry(e, { metodo_pagamento: v })}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                          {e.metodo_pagamento && !METHODS.includes(e.metodo_pagamento) ? (
                            <SelectItem key={e.metodo_pagamento} value={e.metodo_pagamento}>
                              {SYSTEM_METHODS.includes(e.metodo_pagamento) ? (
                                <span className="text-slate-700">{e.metodo_pagamento}</span>
                              ) : (
                                <span className="text-slate-500 italic">{e.metodo_pagamento} (legacy)</span>
                              )}
                            </SelectItem>
                          ) : null}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <AmountInput disabled={!canEdit || e.versato} defaultValue={e.importo}
                        className="h-8 text-sm text-right font-mono-num"
                        onBlur={(ev) => {
                          const v = parseFloat(ev.target.value);
                          if (isFinite(v) && v !== e.importo) updateEntry(e, { importo: v });
                        }} />
                    </td>
                    {allowAbbuoni && (
                      <td className="px-3 py-2 text-right">
                        <AmountInput disabled={!canEdit || e.versato} defaultValue={e.abbuono || 0}
                          className="h-8 text-sm text-right font-mono-num"
                          onBlur={(ev) => {
                            const v = parseFloat(ev.target.value || "0");
                            if (isFinite(v) && v !== (e.abbuono || 0)) updateEntry(e, { abbuono: v });
                          }} />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <Input disabled={!canEdit || e.versato} defaultValue={e.note || ""} className="h-8 text-sm"
                        onBlur={(ev) => ev.target.value !== (e.note || "") && updateEntry(e, { note: ev.target.value })} />
                    </td>
                    <td className="px-3 py-2">
                      {e.versato ? (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Versato
                          </Badge>
                          {manualVersato && (
                            <button onClick={() => unmarkVersato(e)} className="text-slate-400 hover:text-slate-700" title="Annulla versamento manuale" data-testid={`unmark-${i}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markVersato(e)} data-testid={`mark-versato-${i}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Segna versato
                        </Button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {canEdit && !e.versato && (
                        <button onClick={() => deleteEntry(e)} className="text-slate-400 hover:text-red-600" data-testid={`detail-delete-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* Draft row (only when can edit) */}
              {canEdit && (
                <tr className={draft.urgente ? "urgent-row" : "bg-slate-50/50"}>
                  <td className="px-3 py-2 text-slate-300">+</td>
                  <td className="px-3 py-2 text-center">
                    <Checkbox checked={draft.urgente} onCheckedChange={(v) => setDraft({ ...draft, urgente: !!v })} />
                  </td>
                  <td className="px-3 py-2"><Input placeholder="Contraente" value={draft.contraente} onChange={(e) => setDraft({ ...draft, contraente: e.target.value })} className="h-8 text-sm" data-testid="agente-draft-contraente" /></td>
                  <td className="px-3 py-2"><Input placeholder="N. polizza" value={draft.numero_polizza} onChange={(e) => setDraft({ ...draft, numero_polizza: e.target.value })} className="h-8 text-sm" data-testid="agente-draft-polizza" /></td>
                  <td className="px-3 py-2">
                    <Select value={draft.metodo_pagamento} onValueChange={(v) => setDraft({ ...draft, metodo_pagamento: v })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2"><AmountInput placeholder="0,00" value={draft.importo} onChange={(e) => setDraft({ ...draft, importo: e.target.value })} className="h-8 text-sm text-right font-mono-num" data-testid="agente-draft-importo" /></td>
                  {allowAbbuoni && (
                    <td className="px-3 py-2"><AmountInput placeholder="0,00" value={draft.abbuono} onChange={(e) => setDraft({ ...draft, abbuono: e.target.value })} className="h-8 text-sm text-right font-mono-num" data-testid="agente-draft-abbuono" /></td>
                  )}
                  <td className="px-3 py-2"><Input placeholder="Note" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} className="h-8 text-sm" /></td>
                  <td colSpan={2}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <div className="border-t border-slate-200 bg-white p-3 flex flex-wrap gap-2 justify-end">
            {multiEnabled && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setMultiOpen(true)}
                disabled={saving}
                data-testid="agente-open-multi-polizza-btn"
              >
                <Layers className="w-4 h-4 mr-2" /> Incasso multiplo
              </Button>
            )}
            <Button onClick={addEntry} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="agente-add-entry">
              <Plus className="w-4 h-4 mr-2" /> {saving ? "Salvataggio…" : "Aggiungi riga"}
            </Button>
          </div>
        )}
      </Card>
      <MultiPolizzaDialog
        open={multiOpen}
        onOpenChange={setMultiOpen}
        sheetId={sheet?.id}
        methods={METHODS}
        allowAbbuoni={allowAbbuoni}
        plurimandatari={plurimandatari}
        companies={companies}
        defaultMethod={draft.metodo_pagamento || "Contante"}
        onSuccess={load}
      />
    </div>
  );
}
